// Per-course, per-side line coverage for the Openings Academy. A run always
// starts at the initial position; progress records accepted leaves, not a
// fragile in-line resume index. Legacy v1 `{step, done}` entries are ignored.

import type { Color } from "./types.ts";

export interface CourseProgress {
  lines: string[];
  started: boolean;
}

export interface CourseCoverage extends CourseProgress {
  covered: number;
  total: number;
  complete: boolean;
}

interface StoredProgress {
  lines: string[];
}

const KEY = "chess.openingProgress";
type Store = Record<string, StoredProgress>;

const progressKey = (slug: string, side: Color): string => `${slug}:${side}`;

function readAll(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const clean: Store = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!value || typeof value !== "object" || !("lines" in value)) continue;
      const lines = (value as { lines?: unknown }).lines;
      if (!Array.isArray(lines) || !lines.every((line) => typeof line === "string" && line.length > 0)) continue;
      clean[key] = { lines: [...new Set(lines)] };
    }
    return clean;
  } catch {
    return {};
  }
}

function writeAll(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Quota/privacy-mode failure: training still works without persistence.
  }
}

export function getProgress(slug: string, side: Color): CourseProgress | undefined {
  const progress = readAll()[progressKey(slug, side)];
  if (!progress) return undefined;
  return { lines: [...progress.lines], started: progress.lines.length > 0 };
}

/** Project stored progress onto the course's current, valid leaf set. */
export function courseCoverage(slug: string, side: Color, validLeaves: readonly string[]): CourseCoverage {
  const valid = new Set(validLeaves);
  const lines = (getProgress(slug, side)?.lines ?? []).filter((line) => valid.has(line));
  const unique = [...new Set(lines)];
  return {
    lines: unique,
    started: unique.length > 0,
    covered: unique.length,
    total: valid.size,
    complete: valid.size > 0 && unique.length === valid.size,
  };
}

export function saveCompletion(
  slug: string,
  side: Color,
  pathKey: string,
  validLeaves: readonly string[],
): CourseCoverage {
  const store = readAll();
  const key = progressKey(slug, side);
  const valid = new Set(validLeaves);
  if (!valid.has(pathKey)) return courseCoverage(slug, side, validLeaves);
  const lines = new Set((store[key]?.lines ?? []).filter((line) => valid.has(line)));
  lines.add(pathKey);
  store[key] = { lines: [...lines] };
  writeAll(store);
  return courseCoverage(slug, side, validLeaves);
}

/** Explicitly clear one side's coverage; restarting a run does not call this. */
export function clearProgress(slug: string, side: Color): void {
  const store = readAll();
  delete store[progressKey(slug, side)];
  writeAll(store);
}
